import {
  Badge,
  Button,
  DataTable,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  type TanstackReactTable,
} from "@devhop/ui";
import { RiEditLine } from "@remixicon/react";
import { Bell, CheckCircle2, XCircle } from "lucide-react";
import { useState } from "react";
import { useAdjustFeedOrderQuantity } from "@/modules/cf-feed/presentation/api/queries";
import type { FeedCustomerRow } from "@/modules/cf-feed/presentation/api/types";
import { useBoundedInfiniteScroll } from "@/modules/cf-feed/presentation/lib/use-bounded-infinite-scroll";
import { useSendReminder } from "@/modules/cf-message/presentation/api/queries";
import { formatDateTimeLocal } from "@/shared/lib/date-time";
import { formatKip } from "./table-helpers";

const keepInfiniteRowsVisible = () => undefined;

export type FeedCustomersTableProps = {
  data: FeedCustomerRow[];
  isLoading?: boolean;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  onLoadMore?: () => void;
  total?: number;
  feedId: string;
};

function DmStatusBadge({
  status,
  label,
}: {
  status: FeedCustomerRow["autoReplyDmStatus"];
  label: string;
}) {
  if (status === "sent") {
    return (
      <Badge
        variant="success"
        leadingIcon={<CheckCircle2 className="size-3" />}
      >
        {label}
      </Badge>
    );
  }
  if (status === "failed") {
    return (
      <Badge variant="destructive" leadingIcon={<XCircle className="size-3" />}>
        {label}: ບໍ່ສຳເລັດ
      </Badge>
    );
  }
  return null;
}

function ReminderCell({ row }: { row: FeedCustomerRow }) {
  const send = useSendReminder();
  const sent = row.reminderDmStatus === "sent";
  const failed = row.reminderDmStatus === "failed";

  return (
    <div className="flex flex-col items-start gap-1">
      <Button
        size="sm"
        variant={sent ? "outline" : "default"}
        disabled={send.isPending || row.status === "cancelled"}
        onClick={() => send.mutate(row.orderId)}
      >
        <Bell className="size-3.5" />
        {sent ? "ສົ່ງຄືນ" : "ສົ່ງທວງຍອດ"}
      </Button>
      {sent ? (
        <span className="text-[10px] text-emerald-600">ສົ່ງແລ້ວ</span>
      ) : null}
      {failed ? (
        <span className="text-[10px] text-destructive">ສົ່ງບໍ່ສຳເລັດ</span>
      ) : null}
    </div>
  );
}

export function FeedCustomersTable({
  data,
  isLoading,
  hasNextPage = false,
  isFetchingNextPage = false,
  onLoadMore,
  total = data.length,
  feedId,
}: FeedCustomersTableProps) {
  const { scrollRootRef, sentinelRef } = useBoundedInfiniteScroll({
    hasNextPage,
    isFetchingNextPage,
    onLoadMore,
  });
  const adjustQuantity = useAdjustFeedOrderQuantity(feedId);
  const [adjustTarget, setAdjustTarget] = useState<{
    row: FeedCustomerRow;
    quantity: string;
    idempotencyKey: string;
  } | null>(null);

  const columns: TanstackReactTable.ColumnDef<FeedCustomerRow>[] = [
    {
      id: "orderReference",
      header: "ອ້າງອີງອໍເດີ",
      cell: ({ row }) => (
        <span className="break-all">
          {row.original.orderCode ?? row.original.orderId}
        </span>
      ),
      size: 150,
    },
    {
      id: "customer",
      header: "ຜູ້ສັ່ງຊື້",
      cell: ({ row }) => row.original.customerName,
    },
    {
      id: "quantity",
      header: "ຈຳນວນ",
      cell: ({ row }) => row.original.quantity,
      size: 80,
    },
    {
      id: "cfCode",
      header: "ລະຫັດສິນຄ້າ",
      cell: ({ row }) => (
        <span className="font-mono text-xs">{row.original.cfCode}</span>
      ),
      size: 110,
    },
    {
      id: "total",
      header: "ລວມເງິນ",
      cell: ({ row }) => formatKip(row.original.totalPrice),
      size: 120,
    },
    {
      id: "createdAt",
      header: "ເວລາ",
      cell: ({ row }) => (
        <span className="text-muted-foreground text-xs">
          {formatDateTimeLocal(row.original.createdAt)}
        </span>
      ),
      size: 160,
    },
    {
      id: "status",
      header: "ສະຖານະ",
      cell: ({ row }) => row.original.statusLabel,
      size: 110,
    },
    {
      id: "provide",
      header: "ການສັ່ງຊື້",
      cell: ({ row }) => (
        <Badge
          variant={
            row.original.orderProvide === "auto" ? "default" : "secondary"
          }
        >
          {row.original.orderProvideLabel}
        </Badge>
      ),
      size: 110,
    },
    {
      id: "dm",
      header: "DM ຕອບກັບ",
      cell: ({ row }) => (
        <DmStatusBadge status={row.original.autoReplyDmStatus} label="ສົ່ງແລ້ວ" />
      ),
      size: 130,
    },
    {
      id: "reminder",
      header: "ທວງຍອດ",
      cell: ({ row }) => <ReminderCell row={row.original} />,
      size: 150,
    },
    {
      id: "adjustQuantity",
      header: "ປັບຈຳນວນ",
      cell: ({ row }) => (
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={!row.original.canAdjustQuantity}
          onClick={() =>
            setAdjustTarget({
              row: row.original,
              quantity: String(Math.max(0, row.original.quantity - 1)),
              idempotencyKey: crypto.randomUUID(),
            })
          }
        >
          <RiEditLine className="size-3.5" />
          ຄືນ stock
        </Button>
      ),
      size: 130,
    },
  ];

  const parsedTargetQuantity = Number(adjustTarget?.quantity ?? "");
  const validTargetQuantity = Boolean(
    adjustTarget &&
      Number.isSafeInteger(parsedTargetQuantity) &&
      parsedTargetQuantity >= 0 &&
      parsedTargetQuantity < adjustTarget.row.quantity,
  );

  return (
    <div className="space-y-2">
      <div
        ref={scrollRootRef}
        className="max-h-[36rem] overflow-auto overscroll-contain rounded-md"
      >
        <DataTable<FeedCustomerRow, unknown>
          data={data}
          columns={columns}
          isLoading={isLoading}
          noDataMessage="ຍັງບໍ່ມີລູກຄ້າ"
          enablePagination={false}
          onPaginationChange={keepInfiniteRowsVisible}
          limit={Math.max(data.length, 1)}
          offset={0}
          totalCount={data.length}
        />
        <div className="flex flex-wrap items-center justify-between gap-2 p-2 text-muted-foreground text-xs">
          <span>
            ສະແດງແລ້ວ {data.length} ຈາກທັງໝົດ {total} ລາຍການ
          </span>
          {hasNextPage && onLoadMore ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={isFetchingNextPage}
              onClick={onLoadMore}
            >
              {isFetchingNextPage ? "ກຳລັງໂຫຼດ..." : "ໂຫຼດລາຍການເພີ່ມ"}
            </Button>
          ) : null}
        </div>
        {hasNextPage ? (
          <div
            ref={sentinelRef}
            className="flex min-h-10 items-center justify-center text-muted-foreground text-xs"
            aria-live="polite"
          >
            {isFetchingNextPage ? "ກຳລັງໂຫຼດເພີ່ມ..." : "ເລື່ອນເພື່ອໂຫຼດເພີ່ມ"}
          </div>
        ) : null}
      </div>

      <Dialog
        open={adjustTarget != null}
        onOpenChange={(open) => {
          if (!open && !adjustQuantity.isPending) setAdjustTarget(null);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>ປັບຈຳນວນ CF ແລະ ຄືນ stock</DialogTitle>
          </DialogHeader>
          {adjustTarget ? (
            <div className="space-y-4">
              <div className="rounded-md border bg-muted/40 p-3 text-sm">
                <p className="font-medium">{adjustTarget.row.customerName}</p>
                <p className="text-muted-foreground">
                  {adjustTarget.row.cfCode} · ຈຳນວນເກົ່າ{" "}
                  {adjustTarget.row.quantity}
                  ຊິ້ນ
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="cf-order-target-quantity">ຈຳນວນໃໝ່</Label>
                <Input
                  id="cf-order-target-quantity"
                  type="number"
                  min={0}
                  max={Math.max(0, adjustTarget.row.quantity - 1)}
                  step={1}
                  value={adjustTarget.quantity}
                  disabled={adjustQuantity.isPending}
                  onChange={(event) =>
                    setAdjustTarget((current) =>
                      current
                        ? { ...current, quantity: event.target.value }
                        : current,
                    )
                  }
                />
                <p className="text-muted-foreground text-xs">
                  ຈຳນວນທີ່ຫຼຸດຈະຖືກຄືນເຂົ້າ stock. ໃສ່ 0 ເພື່ອຄືນທັງໝົດ ແລະ ຍົກເລີກລາຍການນີ້.
                </p>
              </div>
            </div>
          ) : null}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={adjustQuantity.isPending}
              onClick={() => setAdjustTarget(null)}
            >
              ຍົກເລີກ
            </Button>
            <Button
              type="button"
              disabled={!validTargetQuantity || adjustQuantity.isPending}
              onClick={() => {
                if (!adjustTarget || !validTargetQuantity) return;
                adjustQuantity.mutate(
                  {
                    orderId: adjustTarget.row.orderId,
                    quantity: parsedTargetQuantity,
                    idempotencyKey: adjustTarget.idempotencyKey,
                  },
                  { onSuccess: () => setAdjustTarget(null) },
                );
              }}
            >
              {adjustQuantity.isPending ? "ກຳລັງບັນທຶກ..." : "ຢືນຢັນຄືນ stock"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
